/**
 * parts/midway.js — the dressing that fills the space between booths.
 *
 * Bunting poles with balloon clusters (what `createTree` builds — a fairground
 * has no trees down the middle of it) and the catenary lamp strings that give
 * the midway its ceiling.
 *
 * PER-BULB MATERIALS: `twinkle()` writes `bulb.material.opacity`, so each bulb
 * needs its OWN material. Using the memoised `mat()` here would make one bulb's
 * flicker drive every bulb on the midway.
 *
 * @module assets3d/parts/midway
 */

import * as THREE from 'three';
import { ART, shade, rngFor } from '../palette.js';
import { barberStripe, buntingStrip } from '../textures.js';
import { geo, mat, mesh, tube, finial } from './core.js';

/** Warm bulbs for structure, the full set for celebration. */
const BULB_SETS = {
  warm: [0xffd9a0, 0xffe8c4, 0xffc978],
  multi: [0xffd166, 0xff7b54, 0x6fe7c4, 0xc9a0f0],
};

/**
 * Catenary height at parameter t ∈ [0,1], peaking at the ends.
 * @param {number} t @param {number} sag
 * @returns {number}
 */
function catenary(t, sag) {
  const a = 1.9;
  const c = Math.cosh(a);
  return -sag * (c - Math.cosh(a * (2 * t - 1))) / (c - 1);
}

/**
 * A string of carnival lights along the +X axis, hung as a real catenary.
 *
 * @param {number} [length] Metres.
 * @param {number} [count] Bulbs.
 * @param {{ palette?: 'warm'|'multi' }} [opts]
 * @returns {THREE.Group} with `userData.twinkle(elapsed)`.
 */
export function createLampString(length = 8, count = 12, opts = {}) {
  const g = new THREE.Group();
  g.name = 'lamp-string';
  const sag = length * 0.075;
  const colors = BULB_SETS[opts.palette] ?? BULB_SETS.warm;

  const pts = [];
  for (let i = 0; i <= 20; i++) {
    const t = i / 20;
    pts.push(new THREE.Vector3(-length / 2 + t * length, catenary(t, sag), 0));
  }

  // A swept cable rather than a Line: a 1 px line disappears at distance and
  // the bulbs end up floating in a row.
  const cable = mesh(
    tube(pts, 0.011, { key: `cable-${length}-${sag}`, segments: 20, radial: 4 }),
    mat(0x3a3038, { rough: 0.85 }),
    { receive: false },
  );
  g.add(cable);

  const bulbs = [];
  const bulbGeo = geo('midway-bulb', () => new THREE.SphereGeometry(0.055, 7, 5));
  const capGeo = geo('midway-bulb-cap', () => new THREE.CylinderGeometry(0.022, 0.026, 0.026, 6));

  for (let i = 0; i < count; i++) {
    const t = (i + 0.5) / count;
    const x = -length / 2 + t * length;
    const y = catenary(t, sag);
    const c = colors[i % colors.length];

    const cap = mesh(capGeo, mat(0x4a4048, { rough: 0.7 }), { receive: false });
    cap.position.set(x, y - 0.022, 0);
    g.add(cap);

    // Own material per bulb — see the note at the top of the file.
    const b = new THREE.Mesh(bulbGeo, new THREE.MeshBasicMaterial({
      color: c, transparent: true, opacity: 0.95,
    }));
    b.position.set(x, y - 0.072, 0);
    b.name = `bulb-${i}`;
    g.add(b);
    bulbs.push(b);
  }

  /**
   * Chase the bulbs. Phase is offset per bulb so the string ripples rather
   * than pulsing as one block.
   * @param {number} elapsed
   */
  g.userData.twinkle = (elapsed) => {
    for (let i = 0; i < bulbs.length; i++) {
      const k = 0.68 + 0.32 * Math.sin(elapsed * 2.2 + i * 0.9);
      bulbs[i].material.opacity = k;
      bulbs[i].scale.setScalar(0.82 + k * 0.32);
    }
  };
  g.userData.bulbs = bulbs;
  return g;
}

/**
 * A bunting pole with a balloon cluster — the midway's answer to a tree.
 *
 * Keeps the `createTree(height)` signature the hub already calls, because a
 * fairground midway is dressed with poles and balloons, not woodland.
 *
 * @param {number} [height]
 * @returns {THREE.Group}
 */
export function createTree(height = 4) {
  const g = new THREE.Group();
  g.name = 'bunting-pole';
  // Height is the only thing the hub varies, so it seeds the variation.
  const rnd = rngFor(`pole-${height.toFixed(2)}`);

  const poleR = 0.075;
  const pole = mesh(
    geo(`pole-${height}`, () => new THREE.CylinderGeometry(poleR, poleR * 1.15, height, 10)),
    mat(0xffffff, { rough: 0.62, map: barberStripe(ART.red, ART.cream, Math.round(height * 2)) }),
  );
  pole.position.y = height / 2;
  g.add(pole);

  // Planter base, so the pole is standing in something.
  const planter = mesh(
    geo('pole-planter', () => new THREE.CylinderGeometry(0.30, 0.36, 0.30, 12)),
    mat(ART.teal, { rough: 0.8 }),
  );
  planter.position.y = 0.15;
  g.add(planter);

  const soil = mesh(
    geo('pole-soil', () => new THREE.CylinderGeometry(0.27, 0.27, 0.04, 12)),
    mat(shade(ART.grassDark, 0.35), { rough: 1 }),
    { cast: false },
  );
  soil.position.y = 0.30;
  g.add(soil);

  const rim = mesh(
    geo('pole-planter-rim', () => new THREE.CylinderGeometry(0.325, 0.325, 0.06, 12)),
    mat(ART.gold, { rough: 0.5, metal: 0.3 }),
  );
  rim.position.y = 0.29;
  g.add(rim);

  const knob = finial(0.10, ART.gold);
  knob.position.y = height + 0.06;
  g.add(knob);

  // A pennant flying off the top.
  const flag = mesh(
    geo('pole-flag', () => {
      const s = new THREE.Shape();
      s.moveTo(0, 0.16);
      s.lineTo(0.42, 0.06);
      s.lineTo(0, -0.10);
      s.closePath();
      const f = new THREE.ShapeGeometry(s, 1);
      f.rotateY(Math.PI / 2);
      return f;
    }),
    mat(ART.gold, { rough: 0.7, side: THREE.DoubleSide }),
    { receive: false },
  );
  flag.position.set(0, height - 0.14, 0);
  flag.rotation.y = rnd() * Math.PI * 2;
  g.add(flag);

  // Balloon cluster tied two thirds up.
  const clusterY = height * 0.66;
  const palette = [ART.red, ART.teal, ART.gold, ART.plumLight, ART.redLight];
  const n = 4 + Math.floor(rnd() * 3);
  const balloonGeo = geo('cluster-balloon', () => {
    const b = new THREE.SphereGeometry(0.13, 10, 8);
    const pos = b.attributes.position;
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      const t = Math.max(0, -v.y / 0.13);
      const pinch = 1 - 0.7 * Math.pow(t, 2.6);
      v.x *= pinch; v.z *= pinch; v.y *= 1.22;
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    b.computeVertexNormals();
    return b;
  });

  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd() * 0.5;
    const spread = 0.20 + rnd() * 0.16;
    const lift = 0.30 + rnd() * 0.30;
    const b = mesh(balloonGeo, mat(palette[i % palette.length], { rough: 0.26 }));
    b.position.set(Math.cos(a) * spread, clusterY + lift, Math.sin(a) * spread);
    b.scale.setScalar(0.8 + rnd() * 0.35);
    g.add(b);

    const string = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, clusterY, 0),
        b.position.clone().setY(b.position.y - 0.16),
      ]),
      new THREE.LineBasicMaterial({ color: 0xf0e6d2, transparent: true, opacity: 0.45 }),
    );
    g.add(string);
  }

  // A short bunting swag off the pole, tying it into the rest of the midway.
  const strip = buntingStrip([ART.red, ART.cream, ART.teal, ART.gold]);
  const swag = mesh(
    geo('pole-swag', () => {
      const p = new THREE.PlaneGeometry(2.2, 0.26, 16, 1);
      const pos = p.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const t = pos.getX(i) / 2.2 + 0.5;
        pos.setY(i, pos.getY(i) + catenary(t, 0.28));
      }
      p.computeVertexNormals();
      return p;
    }),
    mat(0xffffff, {
      flat: true, map: strip.map, alphaMap: strip.alpha,
      side: THREE.DoubleSide, transparent: true,
    }),
    { cast: false, receive: false },
  );
  swag.position.y = height * 0.80;
  swag.rotation.y = rnd() * Math.PI;
  g.add(swag);

  return g;
}
